"use client";

import { AnimatePresence, motion } from "motion/react";
import { Check } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/utils";

export interface CheckboxCardProperties {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

export function CheckboxCard({
  checked,
  onChange,
  label,
  description,
  icon,
  className,
  disabled = false,
}: CheckboxCardProperties) {
  const handleClick = () => {
    if (!disabled) {
      onChange(!checked);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === "Enter" || e.key === " ") && !disabled) {
      e.preventDefault();
      onChange(!checked);
    }
  };

  return (
    <div
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="checkbox"
      aria-checked={checked}
      tabIndex={disabled ? -1 : 0}
      className={cn(
        "relative flex cursor-pointer items-center space-x-4 rounded-2xl p-5 transition-all duration-300 sm:p-6",
        "focus:outline-none focus-visible:ring-4 focus-visible:ring-strategy-gold/20",
        checked
          ? "bg-strategy-gold/10 border-2 border-strategy-gold"
          : "bg-white border-2 border-gray-300 hover:border-gray-400",
        disabled && "opacity-50 cursor-not-allowed",
        className,
      )}
    >
      {/* Animated selection highlight */}
      {checked && (
        <motion.div
          layoutId="checkbox-card-highlight"
          className="absolute inset-0 z-0 rounded-2xl bg-strategy-gold/10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            type: "spring",
            stiffness: 400,
            damping: 25,
          }}
          style={{ willChange: "opacity" }}
        />
      )}

      {/* Icon/Initials */}
      {icon && (
        <div className="relative z-10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-strategy-gold/20 sm:h-12 sm:w-12">
          <div className={cn(checked ? "text-strategy-gold" : "text-gray-600")}>
            {icon}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 flex-grow">
        <p
          className={cn(
            "text-base font-semibold sm:text-lg",
            checked ? "text-boldness" : "text-gray-900",
          )}
        >
          {label}
        </p>
        {description && (
          <p
            className={cn(
              "text-xs sm:text-sm mt-1",
              checked ? "text-gray-700" : "text-gray-500",
            )}
          >
            {description}
          </p>
        )}
      </div>

      {/* Selection Checkmark */}
      <div className="relative z-10 h-6 w-6 flex-shrink-0">
        <AnimatePresence>
          {checked && (
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              transition={{
                type: "spring",
                stiffness: 500,
                damping: 25,
              }}
              className="flex h-full w-full items-center justify-center rounded-full bg-strategy-gold text-boldness"
              style={{ willChange: "transform, opacity" }}
            >
              <Check className="h-4 w-4" strokeWidth={3} />
            </motion.div>
          )}
        </AnimatePresence>
        {!checked && (
          <div className="h-6 w-6 rounded-full border-2 border-gray-300" />
        )}
      </div>
    </div>
  );
}
